import React, { useMemo } from 'react';
// Corrected import paths
import { useAuth } from '../../hooks/useAuth.js';
import { useProjects } from '../../hooks/useProjects.jsx';
import { userRoles } from '../../data/mockData.js';
import { useNavigate } from 'react-router-dom';
import { Bell, Check, X, Info } from 'lucide-react';
import Breadcrumb from '../common/Breadcrumb.jsx';

// --- UPDATED: This is the action bar for Admins and Funders ---
const ApprovalBar = ({ pendingProjects }) => {
  const navigate = useNavigate();
  const list = Array.isArray(pendingProjects) ? pendingProjects : [];
  const projectCount = list.length;

  if (projectCount === 0) return null; // Don't show the bar if there's nothing to approve

  return (
    // --- UPDATED: Swapped to light red color scheme ---
    <div className="bg-red-50 border-b border-red-200 px-6 py-2.5 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center">
          <span className="relative flex h-3 w-3 mr-3">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
          </span>
          <p className="text-sm font-medium text-red-900">
            You have <span className="font-semibold">{projectCount} new project{projectCount > 1 ? 's' : ''}</span> awaiting approval.
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => navigate('/funding/approval')} // Navigate to the approval page
            className="px-3 py-1.5 text-xs font-medium text-white bg-red-600 rounded-md hover:bg-red-700 shadow-sm"
          >
            Review Requests
          </button>
        </div>
      </div>
    </div>
  );
};

// This is the action bar for Project Managers
const SubmittedBar = ({ pendingProjects }) => {
  const navigate = useNavigate();
  const list = Array.isArray(pendingProjects) ? pendingProjects : [];
  const projectCount = list.length;

  if (projectCount === 0) return null;

  return (
    <div className="bg-white border-b border-gray-200 px-6 py-2.5 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center">
          <Info size={16} className="text-blue-500 mr-2.5" />
          <p className="text-sm font-medium text-gray-700">
            You have <span className="font-semibold text-blue-700">{projectCount} project{projectCount > 1 ? 's' : ''}</span> currently pending approval.
          </p>
        </div>
        <button
          onClick={() => navigate('/projects')} // Navigate to their project list
          className="px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-100 rounded-md hover:bg-blue-200"
        >
          View Projects
        </button>
      </div>
    </div>
  );
};

// Breadcrumb bar component
const BreadcrumbBar = () => {
  return (
    <div className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700 px-6 py-2">
      <Breadcrumb />
    </div>
  );
};

// Main component that decides which bar to show
const ActionBar = () => {
  const { user } = useAuth();
  const { pendingProjectsForUser, projects } = useProjects();

  // This logic is for the PM. It finds their projects that are pending.
  const pmPendingProjects = useMemo(() => {
    if (user?.role !== userRoles.PROJECT_MANAGER) return [];
    if (!Array.isArray(projects)) return [];
    return projects.filter(p => p.status === 'Pending Approval');
  }, [user, projects]);

  // No user, no bar
  if (!user) return null;

  // Get the action bar content based on role
  const getActionContent = () => {
    switch (user.role) {
      case userRoles.ADMIN:
      case userRoles.Funder:
        return <ApprovalBar pendingProjects={pendingProjectsForUser} />;

      case userRoles.PROJECT_MANAGER:
        return <SubmittedBar pendingProjects={pmPendingProjects} />;

      // Finance and Consultants don't see this bar
      case userRoles.FINANCE:
      case userRoles.CONSULTANT:
      default:
        return null;
    }
  };

  return (
    <>
      {/* Always show breadcrumb (it hides itself on dashboard) */}
      <BreadcrumbBar />

      {/* Show role-specific action bar */}
      {getActionContent()}
    </>
  );
};

export default ActionBar;
